import { change, DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeInformedExpectation } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface InformedExpectationFormData {
    student: string;
    formId: string;

    userInitialsOne: string;
    userInitialsTwo: string;
    userInitialsThree: string;
    userInitialsFour: string;
    userInitialsFive: string;
    userInitialsSix: string;
    userInitialsSeven: string;
    userInitialsEight: string;
    userInitialsNine: string;
    signature: string;
    printedName: string;
    todayDate: string;
}

interface InformedExpectationFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<InformedExpectationFormData, InformedExpectationFormProps>;

const validators: Validators<InformedExpectationFormData> = {
    userInitialsOne: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    userInitialsTwo: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    userInitialsThree: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    userInitialsFour: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    userInitialsFive: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    userInitialsSix: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    userInitialsSeven: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    userInitialsEight: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    userInitialsNine: [maxLength({ max: 255 }), required({ message: 'Please enter initial.' })],
    signature: [maxLength({ max: 255 }), required({ message: 'Please enter signature.' })],
    todayDate: [required({ message: 'Please enter date.' })],
    printedName: [maxLength({ max: 255 }), required({ message: 'Please enter printed name.' })],
};

const InformedExpectationForm: React.FunctionComponent<InjectedProps & InformedExpectationFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & InformedExpectationFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>ABA: Informed Consent & Expectations for Working Together</h2>
            <div style={{ textAlign: 'center' }}>(please initial each section and sign at the very bottom)</div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={3}
                wrapperConfig={{
                    label: '',
                }}
            />
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsOne"
                    validate={validators.userInitialsOne}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    <div>
                        <b>Keeping, Changing & Cancelling Appointments</b>
                    </div>
                    It is very important for treatment to be successful that we maintain a treatment schedule that is
                    consistent and mutually attainable. Our providers will be dedicating many hours each week to your
                    family to provide direct service ABA and ABA programming to ensure treatment is delivered
                    consistently and comprehensively.{' '}
                    <span style={{ textDecoration: 'underline' }}>
                        {' '}
                        We will set the schedule each month and ask you to confirm it in writing so there are no
                        misunderstandings.
                    </span>{' '}
                    We also ask that you be on time for your sessions so there are no delays. Because of the large
                    number of hours allotted each week and complexity of setting the schedule in advance,
                    <span style={{ textDecoration: 'underline' }}>
                        {' '}
                        if you need to change the schedule, we ask that you give us at least 1-week notice.
                    </span>{' '}
                    Please keep in mind that therapists may able to attend community outings if applicable. Our
                    expectation is that all the scheduled hours will be maintained, while the treatment setting may
                    change. When there is sickness or last-minute emergencies and you need to cancel, we ask that you
                    notify us (emailing the provider directly, calling their cell phone, and/or calling our main
                    telephone 646-450-6210) as soon as possible to avoid any unnecessary commuting.
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsTwo"
                    validate={validators.userInitialsTwo}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    <div>
                        <b>What To Expect from Us</b>
                    </div>
                    We strive to deliver the best possible care in several ways. First, our staff are highly trained,
                    educated and experienced in ABA. Second, we will always try our best to be on-time to sessions and
                    keep to the planned scheduled, baring unforeseen issues with public transportation which can be
                    unreliable at times. If we need to change or cancel a session, we will give as much notice as
                    possible. Third, we will always communicate professionally and respectfully, being mindful of
                    different cultures and traditions. If you ever have any issues with your provider, we ask that you
                    address it with them directly first. If that does not resolve the problem, you can always speak with
                    our director of ABA services. It is also important to acknowledge that therapy involves potential
                    physical, emotional, and mental risks, including but not limited to the potential for property
                    damage, personal injury, and emotional duress. Proper implementation of Applied Behavior Analysis
                    requires ongoing training and support from a Certified Behavior Analyst, adherence to the treatment
                    plan, and diligence in data collection.
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsThree"
                    validate={validators.userInitialsThree}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    <div>
                        <b>What We Expect from You</b>
                    </div>
                    We expect that you will be on time to sessions, keep to the agreed schedule, give at least 1-week
                    notice if you need to change the treatment setting, and let us know ASAP if you need to cancel due
                    to sickness. We expect you to be courteous and communicate respectfully with our staff. Finally, we
                    expect you to participate in treatment consistently to the best of your ability in a way that
                    supports the patient and his/her treatment goals. You also understand that there are no guarantees
                    that treatment will be successful.
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsFour"
                    validate={validators.userInitialsFour}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    <div>
                        <b>General & Electronic Communication</b>
                    </div>
                    We ask that patients/clients email or call us.{' '}
                    <span style={{ textDecoration: 'underline' }}>Please do not text with our staff or providers.</span>{' '}
                    MPG takes reasonable precautions in following Health Information Privacy recommendations per the
                    U.S. Department of Health & Human Services in the use of unencrypted email to communicate with
                    patients, such as sending receipts, scheduling appointments and coordinating care with collateral
                    providers.
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsFive"
                    validate={validators.userInitialsFive}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    <div>
                        <b>Confidentiality & Sharing Information</b>
                    </div>
                    Treatment from MPG is confidential and requests to share patient health information with those
                    outside of MPG require written release. Written release is not required, however, to share patient
                    information internally between MPG staff. A request in writing can be given that information not be
                    shared within MPG. MPG is obligated by law to break confidentiality if the patient is a danger to
                    themselves or others, under court order or when there is suspicion of child abuse. Further, certain
                    information may be disclosed in instances of non-payment.
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsSix"
                    validate={validators.userInitialsSix}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    <div>
                        <b>Special Procedures for Private Pay Patients/Clients</b>
                    </div>
                    (Skip if you are DOE funded)
                    <div>
                        <p>
                            1. <span style={{ textDecoration: 'underline' }}>48-Hour Cancellation Policy</span> - For
                            all sessions, there is a 48-hour cancellation policy, barring emergencies or last-minute
                            sickness. Notice should be emailed directly to the provider, aba@manhattanpsychologygroup.com
                            or given by telephone at 646-450-6210. If 48-hours notice is not given, the full fee will be
                            charged.
                        </p>
                        <p>
                            2. <span style={{ textDecoration: 'underline' }}>Payment & Insurance</span> - Payment is due
                            in full at the time of service or in advance in the form of cash, check or credit card, made
                            payable to “MANHATTAN PSYCHOLOGY GROUP, PC.” If there is a large number of treatment hours
                            per week, we will email you a weekly balance due email outlining the services rendered and
                            how to make payment. A monthly billing statement can be provided to submit for possible
                            out-of-network reimbursement. We can also provide this receipt weekly upon request. MPG is
                            not in- network with insurance. We will do our best to provide accurate CPT codes. However,
                            it is the patient’s responsibility to be aware of reimbursement policies from their own
                            insurance company.
                        </p>
                        <p>
                            3. <span style={{ textDecoration: 'underline' }}>Late & Non-Payment</span> MPG will charge
                            your credit card on file for any services rendered, missed sessions, or any last-minute
                            cancellations that are not paid within 5 business days of the scheduled time of service.
                            There is a 3.5% administrative fee via PayPal. Non-payment may result in legal action and
                            incur additional fees.
                        </p>
                        <p>
                            4.{' '}
                            <span style={{ textDecoration: 'underline' }}>
                                Payment for Non-Direct Patient Care Services
                            </span>{' '}
                            Telephone and email consultation, supervision, report writing, observations, behavior plans,
                            documentation requests, travel time, court-related requests, and all other non-direct
                            patient care requests are billed at the stated hourly rate, in 15 intervals of the
                            provider’s billable rate. We will always try our best to notify you in advance of any
                            anticipated billable time outside of session. There may not be CPT codes for these services,
                            which means that your insurance company will most likely not reimburse you. Again, it is the
                            patient’s responsibility to check with their insurance regarding reimbursement policies.
                        </p>
                    </div>
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsSeven"
                    validate={validators.userInitialsSeven}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    {' '}
                    <div>
                        <b>Special Procedures for DOE Funded Patients/Clients</b>
                    </div>
                    (Skip if you are private pay) When the DOE is paying for services, it is critical that we complete
                    and mail out the vendor invoice on the first of each month. Since we cannot mail out the invoice
                    without the parent’s signature, we may ask parents to sign the invoice in advance to prevent any
                    delays. In such instances, we will email a picture of the completed invoice to the parent for final
                    approval.
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsEight"
                    validate={validators.userInitialsEight}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    <div>
                        <b>Emergencies</b>
                    </div>
                    In the event of an emergency, patients/parents should call 911 and/or go to their nearest emergency
                    room. Patients should not contact MPG regarding an emergency, as MPG does not provide 24-hour
                    patient coverage.
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <FieldInput
                    name="userInitialsNine"
                    validate={validators.userInitialsNine}
                    wrapperConfig={{
                        classes: {
                            label: 'fullSize',
                        },
                        label: 'Initials *',
                    }}
                />
                <div>
                    <div>
                        <b>Stopping Treatment</b>
                    </div>
                    Sometimes, despite everyone’s best effort, it is not a good fit, and either party must terminate
                    treatment. We will need to stop treatment if there are repeated issues with changing the schedule,
                    cancelling sessions, ending sessions early, starting sessions late and/or interpersonal difficulties
                    that we believe are interfering with our ability to perform ABA. Of course, we will try our best to
                    resolve any issues and give notice when we are concerned about treatment fidelity. We will work with
                    families to develop an individualized plan. If we are not able to reconcile important issues in
                    treatment planning and delivery, we will try to give at least 2-4 weeks notice so that you/we can
                    find replacement providers. Likewise, if you are unhappy with our treatment and want to terminate,
                    we ask that you give at least 2-4 weeks notice.
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <div>
                    <p>
                        By signing below, I verify that I have read and understand the terms of treatment
                        (cancellations, payment, etc.) with MPG outlined above and in the attached ABA Intake Packet.
                    </p>
                </div>
            </div>
            <div style={{ paddingTop: '20px' }}>
                <table>
                    <tr>
                        <td>
                            <FieldInput
                                name="signature"
                                validate={validators.signature}
                                wrapperConfig={{
                                    classes: {
                                        label: 'fullSize',
                                    },
                                    label: 'Signature of Parent/Guardian (if Minor) *',
                                }}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <FieldInput
                                name="printedName"
                                validate={validators.printedName}
                                wrapperConfig={{
                                    classes: {
                                        label: 'fullSize',
                                    },
                                    label: 'Printed Name *',
                                }}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <FieldDate
                                name="todayDate"
                                type="text"
                                date={true}
                                validate={validators.todayDate}
                                wrapperConfig={{
                                    label: 'Date *',
                                }}
                            />
                        </td>
                    </tr>
                </table>
            </div>

            <div className={classNames(styles.actions)}>
                <button
                    type="submit"
                    className={classNames('btn', 'btn-success', 'mr-3')}
                    disabled={!dirty || submitting}
                >
                    <FontAwesomeIcon icon={faSave} /> Confirm and Send
                </button>
            </div>
        </form>
    );
};

export default reduxForm<InformedExpectationFormData, InformedExpectationFormProps>({
    form: 'informed-expectation-form',
    enableReinitialize: true,
    onSubmit: async (
        data: InformedExpectationFormData,
        dispatch: Dispatch<any>,
        props: InformedExpectationFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeInformedExpectation({ ...data }, dispatch);
    },
    onChange: async (
        values: Partial<InformedExpectationFormData>,
        dispatch: Dispatch<any>,
        props: any,
        previousValues: Partial<InformedExpectationFormData>,
    ) => {},
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<InformedExpectationFormData, InformedExpectationFormProps, string>,
    ) {
        props.setIsLoader(false);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(InformedExpectationForm);
